import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Box, Paper, Stack } from "@mui/material";
import {
  setBrandsData,
  setBrandsLoading,
  setBrandsError,
  setBrandsMonthlyData,
  setBrandsMonthlyLoading,
  setBrandsMonthlyError,
  setBrandsReportAttemptData,
  setBrandsReportAttemptLoading,
  setBrandsReportAttemptError,
} from "../../../../../../../../features/optifundDashboardSlice";
import { OptifundDashboardQueryIds } from "../../../../../../../../pages/AssetManagement/Overview/OptifundDashboard/logic/OptifundDashboardQueryIds";
import { useOptifundTabData } from "../../../../hooks/useOptifundTabData";
import AsyncSection from "../../../../AsyncSection";
import {
  BrandDailyRow,
  BrandMonthlyRow,
  BrandReportAttemptRow,
  mapBrandDailyApiRow,
  mapBrandMonthlyApiRow,
  mapBrandReportAttemptApiRow,
} from "../../../../Brands/brandsApiMapping";
import { getBrandMonthLabel, getDefaultBrandMonth, getDefaultBrandYear } from "../../../../Brands/brandsMonthYearOptions";
import BrandsSelector from "./BrandsMobileContent/BrandsSelector";
import BrandsMonthYearFilter from "./BrandsMobileContent/BrandsMonthYearFilter";
import BrandsKpiCards from "./BrandsMobileContent/BrandsKpiCards";
import BrandsIncomeChart from "./BrandsMobileContent/BrandsIncomeChart";
import BrandsDailyTable from "./BrandsMobileContent/BrandsDailyTable";
import BrandsMonthlyTable from "./BrandsMobileContent/BrandsMonthlyTable";
import BrandsReportAttemptTable from "./BrandsMobileContent/BrandsReportAttemptTable";

const ALL_BRANDS = "All brands";
const EMPTY_DAILY_ROWS: BrandDailyRow[] = [];
const EMPTY_MONTHLY_ROWS: BrandMonthlyRow[] = [];
const EMPTY_REPORT_ATTEMPT_ROWS: BrandReportAttemptRow[] = [];

interface BrandsProps {
  userToken: any;
  websiteSharedUid: string;
}

function readMonth(searchParams: URLSearchParams): number {
  const value = Number(searchParams.get("brands_month"));
  return Number.isInteger(value) && value >= 1 && value <= 12 ? value : getDefaultBrandMonth();
}

function readYear(searchParams: URLSearchParams): number {
  const value = Number(searchParams.get("brands_year"));
  return Number.isInteger(value) && value >= 2024 ? value : getDefaultBrandYear();
}

// Mirrors the desktop Brands.tsx's data-fetching wiring exactly (same query
// IDs, same Redux actions, same "all" brand param + client-side filtering).
// Only the rendered content differs (checklist Section 10.3/10.4).
export default function Brands({ userToken, websiteSharedUid }: BrandsProps) {
  const dispatch = useDispatch();
  const {
    brandsData,
    brandsLoading,
    brandsError,
    brandsMonthlyData,
    brandsMonthlyLoading,
    brandsMonthlyError,
    brandsReportAttemptData,
    brandsReportAttemptLoading,
    brandsReportAttemptError,
  } = useSelector((state: any) => state.optifundDashboard);

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedBrand, setSelectedBrand] = useState(() => searchParams.get("brand") ?? ALL_BRANDS);
  const [selectedMonth, setSelectedMonth] = useState(() => readMonth(searchParams));
  const [selectedYear, setSelectedYear] = useState(() => readYear(searchParams));

  const handleSelectedBrandChange = (brand: string) => {
    setSelectedBrand(brand);
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (brand === ALL_BRANDS) next.delete("brand");
        else next.set("brand", brand);
        return next;
      },
      { replace: true }
    );
  };

  const handleSelectedMonthChange = (month: number) => {
    setSelectedMonth(month);
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (month === getDefaultBrandMonth()) next.delete("brands_month");
        else next.set("brands_month", String(month));
        return next;
      },
      { replace: true }
    );
  };

  const handleSelectedYearChange = (year: number) => {
    setSelectedYear(year);
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (year === getDefaultBrandYear()) next.delete("brands_year");
        else next.set("brands_year", String(year));
        return next;
      },
      { replace: true }
    );
  };

  const { refetch: refetchDaily } = useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.brandsDailyId,
    (data) => dispatch(setBrandsData(data)),
    (loading) => dispatch(setBrandsLoading(loading)),
    undefined,
    undefined,
    "all",
    undefined,
    undefined,
    (hasError) => dispatch(setBrandsError(hasError))
  );

  const { refetch: refetchMonthly } = useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.brandsMonthlyId,
    (data) => dispatch(setBrandsMonthlyData(data)),
    (loading) => dispatch(setBrandsMonthlyLoading(loading)),
    undefined,
    undefined,
    "all",
    selectedMonth,
    selectedYear,
    (hasError) => dispatch(setBrandsMonthlyError(hasError))
  );

  const { refetch: refetchReportAttempt } = useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.brandsReportAttemptId,
    (data) => dispatch(setBrandsReportAttemptData(data)),
    (loading) => dispatch(setBrandsReportAttemptLoading(loading)),
    undefined,
    undefined,
    "all",
    selectedMonth,
    selectedYear,
    (hasError) => dispatch(setBrandsReportAttemptError(hasError))
  );

  const dailyRows: BrandDailyRow[] = useMemo(
    () => (Array.isArray(brandsData) ? brandsData.map(mapBrandDailyApiRow) : EMPTY_DAILY_ROWS),
    [brandsData]
  );
  const monthlyRows: BrandMonthlyRow[] = useMemo(
    () => (Array.isArray(brandsMonthlyData) ? brandsMonthlyData.map(mapBrandMonthlyApiRow) : EMPTY_MONTHLY_ROWS),
    [brandsMonthlyData]
  );
  const reportAttemptRows: BrandReportAttemptRow[] = useMemo(
    () =>
      Array.isArray(brandsReportAttemptData)
        ? brandsReportAttemptData.map(mapBrandReportAttemptApiRow)
        : EMPTY_REPORT_ATTEMPT_ROWS,
    [brandsReportAttemptData]
  );

  const brandOptions = useMemo(() => {
    const uniqueBrands = Array.from(
      new Set([...dailyRows.map((row) => row.brand), ...monthlyRows.map((row) => row.brand)])
    ).sort();
    return [ALL_BRANDS, ...uniqueBrands];
  }, [dailyRows, monthlyRows]);

  const filteredDailyRows = useMemo(
    () => dailyRows.filter((row) => selectedBrand === ALL_BRANDS || row.brand === selectedBrand),
    [dailyRows, selectedBrand]
  );
  const filteredMonthlyRows = useMemo(
    () => monthlyRows.filter((row) => selectedBrand === ALL_BRANDS || row.brand === selectedBrand),
    [monthlyRows, selectedBrand]
  );
  const filteredReportAttemptRows = useMemo(
    () => reportAttemptRows.filter((row) => selectedBrand === ALL_BRANDS || row.brand === selectedBrand),
    [reportAttemptRows, selectedBrand]
  );

  const monthlyPeriodLabel = `${getBrandMonthLabel(selectedMonth)} ${selectedYear}`;

  // Each of the three queries is its own independent section - see the
  // desktop Brands.tsx for the full rationale (a slow/failed query never
  // blocks the other two, and Month/Year changes don't reset the whole tab).
  const dailyReady = Array.isArray(brandsData);
  const monthlyReady = Array.isArray(brandsMonthlyData);
  const reportAttemptReady = Array.isArray(brandsReportAttemptData);
  const dailySectionLoading = brandsLoading || (!dailyReady && !brandsError);
  const monthlySectionLoading = brandsMonthlyLoading || (!monthlyReady && !brandsMonthlyError);
  const reportAttemptSectionLoading = brandsReportAttemptLoading || (!reportAttemptReady && !brandsReportAttemptError);

  return (
    <Stack spacing={2.5}>
      <Paper variant="outlined" sx={{ p: "13px 14px", borderColor: "#ececec" }}>
        <Stack spacing={1.25}>
          <Box sx={{ maxWidth: 320 }}>
            <BrandsSelector options={brandOptions} selectedBrand={selectedBrand} onSelectedBrandChange={handleSelectedBrandChange} />
          </Box>
          <BrandsMonthYearFilter
            selectedMonth={selectedMonth}
            onSelectedMonthChange={handleSelectedMonthChange}
            selectedYear={selectedYear}
            onSelectedYearChange={handleSelectedYearChange}
            disabled={brandsMonthlyLoading || brandsReportAttemptLoading}
          />
        </Stack>
      </Paper>

      <AsyncSection
        isLoading={monthlySectionLoading}
        isError={brandsMonthlyError}
        onRetry={refetchMonthly}
        skeletonHeight={640}
        errorMessage="Couldn't load the Brands Performance - Monthly data."
      >
        <Stack spacing={2.5}>
          <BrandsKpiCards rows={filteredMonthlyRows} />
          <BrandsIncomeChart rows={filteredMonthlyRows} periodLabel={monthlyPeriodLabel} />
          <BrandsMonthlyTable rows={filteredMonthlyRows} periodLabel={monthlyPeriodLabel} />
        </Stack>
      </AsyncSection>

      <AsyncSection
        isLoading={reportAttemptSectionLoading}
        isError={brandsReportAttemptError}
        onRetry={refetchReportAttempt}
        skeletonHeight={320}
        errorMessage="Couldn't load the Brand Reporting Attempt Analysis."
      >
        <BrandsReportAttemptTable rows={filteredReportAttemptRows} periodLabel={monthlyPeriodLabel} />
      </AsyncSection>

      <AsyncSection
        isLoading={dailySectionLoading}
        isError={brandsError}
        onRetry={refetchDaily}
        skeletonHeight={320}
        errorMessage="Couldn't load the Brands Performance - Daily data."
      >
        <BrandsDailyTable rows={filteredDailyRows} />
      </AsyncSection>
    </Stack>
  );
}
